// Auth context: subscribes to authStore + exposes login/logout helpers.
// Mounted once at the app root so any descendant can call useAuth().

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authStore, type AuthUser } from './auth-store';
import { payloadLogin, payloadLogout, payloadMe, ApiError } from './api';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  /** True iff there's a valid (non-expired) session loaded. */
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function isLive(exp: number | null): boolean {
  if (exp == null) return true; // no exp known — trust the token until 401 fires
  // Compare in seconds; 30s skew so a token about to expire isn't treated live.
  return exp * 1000 - Date.now() > 30_000;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(authStore.getUser());
  const [token, setToken] = useState<string | null>(authStore.getToken());
  // Loading reflects "haven't tried to validate the persisted session yet".
  // Stays false in this scaffold; PR 2.7 may add a `payloadMe()` boot probe
  // to invalidate stale tokens on first paint.
  const [loading] = useState(false);

  // Re-render whenever the auth store changes (login, logout, refresh).
  useEffect(() => {
    return authStore.subscribe(() => {
      setUser(authStore.getUser());
      setToken(authStore.getToken());
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await payloadLogin(email, password);
    if (!res.token) {
      throw new ApiError(401, 'Login response missing token');
    }
    // Seed the store so subsequent payloadMe() carries the JWT.
    authStore.setSession(res.token, res.user, res.exp);
    // /auth/login doesn't return staffRole — only /auth/me does. Hydrate
    // it now so the sidebar can render correctly on first paint. Failure
    // here doesn't block login (user gets sidebar-without-staffRole-gating
    // until next /auth/me call); we just log and move on.
    try {
      const me = await payloadMe();
      if (me.user) authStore.setUser(me.user as AuthUser);
    } catch {
      /* swallow — sidebar will fall back to no-access view until next /me */
    }
  }, []);

  const logout = useCallback(async () => {
    await payloadLogout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: Boolean(token) && isLive(authStore.getExp()),
      login,
      logout,
    }),
    [user, token, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
